// src/autodesk_usage/hooks/useCompanyProductUsage.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCompanyProductUsageSummary,
  type CorporateProductUsageSummary,
} from "../api/corporate";

export interface UseCompanyProductUsageResult {
  data?: CorporateProductUsageSummary[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCompanyNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(new Set(list.map((item) => item.trim()).filter(Boolean))).sort();
}

function normalizeCompanyProductUsagePayload(
  payload: unknown
): CorporateProductUsageSummary[] {
  if (Array.isArray(payload)) {
    return payload as CorporateProductUsageSummary[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateProductUsageSummary[];
  }
  return [];
}

export function useCompanyProductUsage(
  companyName?: string | string[] | null
): UseCompanyProductUsageResult {
  const companyNames = normalizeCompanyNames(companyName);
  const companyKey = companyNames.join("|");
  const query = useQuery<CorporateProductUsageSummary[]>({
    queryKey: ["company-product-usage", companyKey],
    queryFn: async () => {
      if (!companyNames.length) return [];
      const res = await getCompanyProductUsageSummary(companyNames);
      return normalizeCompanyProductUsagePayload(res.data);
    },
    enabled: companyNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
